/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError } from 'axios';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApiResponse, PagedApiResponse, LoginRequest, LoginResponse, Mentee, MenteeProfile, Mentor, Company, MentorProfileResponse, UpdateMentorProfileRequest, CategoryResponse, MentorEducationResponse, CreateMentorEducationRequest, UpdateMentorEducationRequest, MenteeProfileResponse, UpdateMenteeProfileRequest, MentorProjectResponse, CreateMentorProjectRequest, UpdateMentorProjectRequest, MentorSkillResponse, CreateMentorSkillRequest, UpdateMentorSkillRequest, MentorWorkExperienceResponse, CreateMentorWorkExperienceRequest, UpdateMentorWorkExperienceRequest, MentorCertificateResponse, CreateMentorCertificateRequest, UpdateMentorCertificateRequest, MentorAvailabilityResponse, CreateMentorAvailabilityRequest, UpdateMentorAvailabilityRequest, BulkCreateAvailabilityRequest, BootcampResponse, CreateBootcampRequest, UpdateBootcampRequest, BootcampApplicationResponse, UpdateBootcampApplicationRequest, MentorServiceResponse, CreateMentorServiceRequest, UpdateMentorServiceRequest, MenteeEducationResponse, CreateMenteeEducationRequest, UpdateMenteeEducationRequest, MenteeProjectResponse, CreateMenteeProjectRequest, UpdateMenteeProjectRequest, MenteeSkillResponse, CreateMenteeSkillRequest, UpdateMenteeSkillRequest, MenteeWorkExperienceResponse, CreateMenteeWorkExperienceRequest, UpdateMenteeWorkExperienceRequest, MenteeCertificateResponse, CreateMenteeCertificateRequest, UpdateMenteeCertificateRequest, MentorSearchRequest, MentorSearchResponse, MentorDetailResponse, MentorSearchFilters, MentorRatingResponse, PagedResult, BootcampSearchResponse, BootcampDetailResponse, BootcampFiltersResponse, BootcampApplicationRequest, MentorBookingRequest, MentorBookingResponse, MenteeSearchRequest, MenteeSearchResponse, MenteeSearchFilters, MenteeDetailResponse, CompanyProfileResponse, UpdateCompanyProfileRequest, ChangePasswordRequest, JobPostingResponse, CreateJobPostingRequest, UpdateJobPostingRequest, JobApplicationResponse, UpdateJobApplicationRequest, InterviewResponse, CreateInterviewRequest, UpdateInterviewRequest, PublicJobPostingSearchRequest, PublicJobPostingResponse, PublicJobPostingDetailResponse, JobPostingFiltersResponse, JobApplicationRequest, AdminCompanyResponse, UpdateAdminCompanyRequest, CandidateFavoriteResponse, CandidatePoolResponse, CandidateFilterRequest, SubscriptionResponse, CurrentSubscriptionResponse, CreateSubscriptionRequest, RenewSubscriptionRequest, FakePaymentRequest, FakePaymentResponse, DevFakePaymentFeatureResponse, DevFakeSubscriptionCompleteRequest, DevFakeAdvertisementResponse, ParamPaymentInitResponse, AdminSubscriptionResponse, AdminManageSubscriptionRequest, SubscriptionPricingResponse, UpdateSubscriptionPricingRequest, NotificationItem, AdvertisementSlotResponse, AdvertisementResponse, AdvertisementPosition } from '../types';
import { ApiDefinition } from '../config';
import { queryClient } from '../lib/queryClient';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: ApiDefinition.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - token ekle
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - hata yönetimi ve token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // Error logging - sadece 500+ hatalarını logla (client error'ları değil)
        if (error.response && error.response.status >= 500) {
          // Error logging servisini dinamik import ile yükle (circular dependency önlemek için)
          import('./errorLogger').then(({ errorLogger }) => {
            errorLogger.logError(
              new Error(`API Error: ${error.response?.status} - ${error.message}`),
              {
                component: 'ApiService',
                action: originalRequest.method?.toUpperCase() || 'UNKNOWN',
                additionalData: {
                  url: originalRequest.url,
                  method: originalRequest.method,
                  status: error.response?.status,
                  statusText: error.response?.statusText,
                },
              }
            ).catch(() => {
              // Logging hatası - sessizce devam et
            });
          });
        }
        
        // Public endpoint'ler - login'e yönlendirme yapma
        // Public sayfalar için kullanılan tüm endpoint'ler
        const publicEndpoints = [
          // Mentor endpoints
          '/api/mentor/search',
          '/api/mentor/availability',
          '/api/mentor/published-bootcamps',
          '/api/mentor/education/mentor/',
          '/api/mentor/skill/mentor/',
          '/api/mentor/project/mentor/',
          '/api/mentor/work-experience/mentor/',
          '/api/mentor/certificate/mentor/',
          '/api/mentor/bootcamp/mentor/',
          '/api/mentor/service/mentor/',
          // Mentee endpoints
          '/api/mentee/search',
          '/api/mentee/detail/',
          '/api/mentee/skill/mentee/',
          '/api/mentee/education/mentee/',
          '/api/mentee/work-experience/mentee/',
          '/api/mentee/project/mentee/',
          '/api/mentee/certificate/mentee/',
          // Bootcamp endpoints
          '/api/bootcamp/',
          '/api/bootcamp/search',
          '/api/bootcamp/filters',
          // Job posting endpoints
          '/api/job-posting/',
          '/api/job-posting/public',
          '/api/job-posting/search',
          // Category endpoints
          '/api/category/',
          // Advertisement endpoints
          '/api/advertisement/',
          // Auth endpoints (login, register)
          '/api/auth/login',
          '/api/auth/login-mentee',
          '/api/auth/login-mentor',
          '/api/auth/login-company',
          '/api/auth/login-admin',
          '/api/auth/register',
          // Public GET endpoints genel olarak
        ];
        
        // Public endpoint kontrolü
        const isPublicEndpoint = originalRequest.url ? publicEndpoints.some(endpoint => 
          originalRequest.url!.includes(endpoint)
        ) : false;
        
        // Public endpoint'ler için 401 hatasında direkt hata döndür, token refresh yapma
        if (error.response?.status === 401 && isPublicEndpoint) {
          return Promise.reject(error);
        }
        
        // Private endpoint'ler için 401 hatasında token refresh yap
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await this.refreshToken(refreshToken);
              if (response.success && response.data) {
                const newToken = response.data.token;
                const newRefreshToken = response.data.refreshToken;
                
                localStorage.setItem('token', newToken);
                if (newRefreshToken) {
                  localStorage.setItem('refreshToken', newRefreshToken);
                }
                
                // Orijinal isteği yeni token ile tekrar dene
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                return this.api(originalRequest);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
            }
          }
          
          // Refresh başarısız, logout yap
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          queryClient.clear();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /** Aynı e-postanın birden fazla rolde olması; tek tabloda arama (tercih edilen). */
  async login(
    credentials: LoginRequest,
    as?: 'Mentee' | 'Mentor' | 'Company' | 'Admin'
  ): Promise<LoginResponse> {
    const path = !as
      ? '/api/auth/login'
      : {
          Mentee: '/api/auth/login-mentee',
          Mentor: '/api/auth/login-mentor',
          Company: '/api/auth/login-company',
          Admin: '/api/auth/login-admin',
        }[as];
    const response: AxiosResponse<LoginResponse> = await this.api.post(path, credentials);
    return response.data;
  }

  async registerMentee(data: any): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/api/auth/register-mentee', data);
    return response.data;
  }

  async registerMentor(data: any): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/api/auth/register-mentor', data);
    return response.data;
  }

  async registerCompany(data: any): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/api/auth/register-company', data);
    return response.data;
  }

  async resendVerificationEmail(email: string): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.post('/api/auth/resend-verification-email', { email });
    return response.data;
  }

  // Mentee endpoints
  async getMentees(page: number = 1, pageSize: number = 10, search?: string): Promise<ApiResponse<Mentee[]>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);

    const response: AxiosResponse<ApiResponse<Mentee[]>> = await this.api.get(`/api/mentee?${params}`);
    return response.data;
  }

  async getMenteeById(id: number): Promise<ApiResponse<Mentee>> {
    const response: AxiosResponse<ApiResponse<Mentee>> = await this.api.get(`/api/mentee/${id}`);
    return response.data;
  }

  async createMentee(mentee: Partial<Mentee>): Promise<ApiResponse<Mentee>> {
    const response: AxiosResponse<ApiResponse<Mentee>> = await this.api.post('/api/mentee', mentee);
    return response.data;
  }

  async updateMentee(id: number, mentee: Partial<Mentee>): Promise<ApiResponse<Mentee>> {
    const response: AxiosResponse<ApiResponse<Mentee>> = await this.api.put(`/api/mentee/${id}`, mentee);
    return response.data;
  }

  async deleteMentee(id: number): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/api/mentee/${id}`);
    return response.data;
  }

  // Mentee Profile endpoints
  async getMenteeProfiles(page: number = 1, pageSize: number = 10, search?: string): Promise<ApiResponse<MenteeProfile[]>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);

    const response: AxiosResponse<ApiResponse<MenteeProfile[]>> = await this.api.get(`/api/mentee/profile?${params}`);
    return response.data;
  }

  async getMenteeProfileById(id: number): Promise<ApiResponse<MenteeProfile>> {
    const response: AxiosResponse<ApiResponse<MenteeProfile>> = await this.api.get(`/api/mentee/profile/${id}`);
    return response.data;
  }

  async createMenteeProfile(profile: Partial<MenteeProfile>): Promise<ApiResponse<MenteeProfile>> {
    const response: AxiosResponse<ApiResponse<MenteeProfile>> = await this.api.post('/api/mentee/profile', profile);
    return response.data;
  }


  // Mentee Booking endpoints
  async getMenteeBookings(page: number = 1, pageSize: number = 10, search?: string): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);

    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/api/mentee/booking?${params}`);
    return response.data;
  }

  async createMenteeBooking(booking: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentee/booking', booking);
    return response.data;
  }

  // Mentor endpoints
  async getMentors(page: number = 1, pageSize: number = 10, search?: string): Promise<ApiResponse<Mentor[]>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);

    const response: AxiosResponse<ApiResponse<Mentor[]>> = await this.api.get(`/api/mentor?${params}`);
    return response.data;
  }

  // Mentor Profile endpoints
  async getMentorProfile(): Promise<ApiResponse<MentorProfileResponse>> {
    const response: AxiosResponse<ApiResponse<MentorProfileResponse>> = await this.api.get('/api/mentor/profile');
    return response.data;
  }

  async updateMentorProfile(data: UpdateMentorProfileRequest): Promise<ApiResponse<MentorProfileResponse>> {
    const response: AxiosResponse<ApiResponse<MentorProfileResponse>> = await this.api.put('/api/mentor/profile', data);
    return response.data;
  }

  async uploadMentorProfileImage(file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<ApiResponse<string>> = await this.api.post('/api/mentor/profile/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Categories
  async getCategories(): Promise<ApiResponse<CategoryResponse[]>> {
    const response: AxiosResponse<ApiResponse<CategoryResponse[]>> = await this.api.get('/api/categories');
    return response.data;
  }

  // Mentor Education endpoints
  async getMentorEducations(mentorId?: number): Promise<ApiResponse<MentorEducationResponse[]>> {
    const url = mentorId ? `/api/mentor/education/mentor/${mentorId}` : '/api/mentor/education';
    const response: AxiosResponse<ApiResponse<MentorEducationResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorEducation(id: number): Promise<ApiResponse<MentorEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MentorEducationResponse>> = await this.api.get(`/api/mentor/education/${id}`);
    return response.data;
  }

  async createMentorEducation(data: CreateMentorEducationRequest): Promise<ApiResponse<MentorEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MentorEducationResponse>> = await this.api.post('/api/mentor/education', data);
    return response.data;
  }

  async updateMentorEducation(id: number, data: UpdateMentorEducationRequest): Promise<ApiResponse<MentorEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MentorEducationResponse>> = await this.api.put(`/api/mentor/education/${id}`, data);
    return response.data;
  }

  async deleteMentorEducationById(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/education/${id}`);
    return response.data;
  }

  // Mentor Project endpoints
  async getMentorProjects(mentorId?: number): Promise<ApiResponse<MentorProjectResponse[]>> {
    const url = mentorId ? `/api/mentor/project/mentor/${mentorId}` : '/api/mentor/project';
    const response: AxiosResponse<ApiResponse<MentorProjectResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorProject(id: number): Promise<ApiResponse<MentorProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MentorProjectResponse>> = await this.api.get(`/api/mentor/project/${id}`);
    return response.data;
  }

  async createMentorProject(data: CreateMentorProjectRequest): Promise<ApiResponse<MentorProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MentorProjectResponse>> = await this.api.post('/api/mentor/project', data);
    return response.data;
  }

  async updateMentorProject(id: number, data: UpdateMentorProjectRequest): Promise<ApiResponse<MentorProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MentorProjectResponse>> = await this.api.put(`/api/mentor/project/${id}`, data);
    return response.data;
  }

  async deleteMentorProject(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/project/${id}`);
    return response.data;
  }

  // Mentor Skill endpoints
  async getMentorSkills(mentorId?: number): Promise<ApiResponse<MentorSkillResponse[]>> {
    const url = mentorId ? `/api/mentor/skill/mentor/${mentorId}` : '/api/mentor/skill';
    const response: AxiosResponse<ApiResponse<MentorSkillResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorSkill(id: number): Promise<ApiResponse<MentorSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MentorSkillResponse>> = await this.api.get(`/api/mentor/skill/${id}`);
    return response.data;
  }

  async createMentorSkill(data: CreateMentorSkillRequest): Promise<ApiResponse<MentorSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MentorSkillResponse>> = await this.api.post('/api/mentor/skill', data);
    return response.data;
  }

  async updateMentorSkill(id: number, data: UpdateMentorSkillRequest): Promise<ApiResponse<MentorSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MentorSkillResponse>> = await this.api.put(`/api/mentor/skill/${id}`, data);
    return response.data;
  }

  async deleteMentorSkill(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/skill/${id}`);
    return response.data;
  }

  // Mentee Profile endpoints
  async getMenteeProfile(): Promise<ApiResponse<MenteeProfileResponse>> {
    console.log('API: getMenteeProfile çağrısı yapılıyor...');
    const response: AxiosResponse<ApiResponse<MenteeProfileResponse>> = await this.api.get('/api/mentee/profile');
    console.log('API: getMenteeProfile yanıtı:', response.data);
    return response.data;
  }

  async updateMenteeProfile(data: UpdateMenteeProfileRequest): Promise<ApiResponse<MenteeProfileResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeProfileResponse>> = await this.api.put('/api/mentee/profile', data);
    return response.data;
  }

  async uploadMenteeProfileImage(file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentee/profile/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Company endpoints (gelecekte eklenecek)
  async getCompanies(page: number = 1, pageSize: number = 10, search?: string): Promise<ApiResponse<Company[]>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);

    const response: AxiosResponse<ApiResponse<Company[]>> = await this.api.get(`/api/company?${params}`);
    return response.data;
  }

  // Mentor Work Experience endpoints
  async getMentorWorkExperiences(mentorId?: number): Promise<ApiResponse<MentorWorkExperienceResponse[]>> {
    const url = mentorId ? `/api/mentor/work-experience/mentor/${mentorId}` : '/api/mentor/work-experience';
    const response: AxiosResponse<ApiResponse<MentorWorkExperienceResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorWorkExperience(id: number): Promise<ApiResponse<MentorWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorWorkExperienceResponse>> = await this.api.get(`/api/mentor/work-experience/${id}`);
    return response.data;
  }

  async createMentorWorkExperience(data: CreateMentorWorkExperienceRequest): Promise<ApiResponse<MentorWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorWorkExperienceResponse>> = await this.api.post('/api/mentor/work-experience', data);
    return response.data;
  }

  async updateMentorWorkExperience(id: number, data: UpdateMentorWorkExperienceRequest): Promise<ApiResponse<MentorWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorWorkExperienceResponse>> = await this.api.put(`/api/mentor/work-experience/${id}`, data);
    return response.data;
  }

  async deleteMentorWorkExperience(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/work-experience/${id}`);
    return response.data;
  }

  // Mentor Certificate endpoints
  async getMentorCertificates(mentorId?: number): Promise<ApiResponse<MentorCertificateResponse[]>> {
    const url = mentorId ? `/api/mentor/certificate/mentor/${mentorId}` : '/api/mentor/certificate';
    const response: AxiosResponse<ApiResponse<MentorCertificateResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorCertificate(id: number): Promise<ApiResponse<MentorCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MentorCertificateResponse>> = await this.api.get(`/api/mentor/certificate/${id}`);
    return response.data;
  }

  async createMentorCertificate(data: CreateMentorCertificateRequest): Promise<ApiResponse<MentorCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MentorCertificateResponse>> = await this.api.post('/api/mentor/certificate', data);
    return response.data;
  }

  async updateMentorCertificate(id: number, data: UpdateMentorCertificateRequest): Promise<ApiResponse<MentorCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MentorCertificateResponse>> = await this.api.put(`/api/mentor/certificate/${id}`, data);
    return response.data;
  }

  async deleteMentorCertificate(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/certificate/${id}`);
    return response.data;
  }

  // Mentor Availability endpoints
  async getMentorAvailabilities(): Promise<ApiResponse<MentorAvailabilityResponse[]>> {
    const response: AxiosResponse<ApiResponse<MentorAvailabilityResponse[]>> = await this.api.get('/api/mentor/availability');
    return response.data;
  }


  async createMentorAvailability(data: CreateMentorAvailabilityRequest): Promise<ApiResponse<MentorAvailabilityResponse>> {
    const response: AxiosResponse<ApiResponse<MentorAvailabilityResponse>> = await this.api.post('/api/mentor/availability', data);
    return response.data;
  }

  async updateMentorAvailability(id: number, data: UpdateMentorAvailabilityRequest): Promise<ApiResponse<MentorAvailabilityResponse>> {
    const response: AxiosResponse<ApiResponse<MentorAvailabilityResponse>> = await this.api.put(`/api/mentor/availability/${id}`, data);
    return response.data;
  }

  async deleteMentorAvailability(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/availability/${id}`);
    return response.data;
  }

  async bulkCreateAvailability(data: BulkCreateAvailabilityRequest): Promise<ApiResponse<MentorAvailabilityResponse[]>> {
    const response: AxiosResponse<ApiResponse<MentorAvailabilityResponse[]>> = await this.api.post('/api/mentor/availability/bulk', data);
    return response.data;
  }

  async bulkDeleteAvailability(ids: number[]): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete('/api/mentor/availability/bulk', { data: ids });
    return response.data;
  }

  // Mentor Bootcamp endpoints
  async getBootcamps(): Promise<ApiResponse<BootcampResponse[]>> {
    const response: AxiosResponse<ApiResponse<BootcampResponse[]>> = await this.api.get('/api/mentor/bootcamp');
    return response.data;
  }

  async getBootcamp(id: number): Promise<ApiResponse<BootcampResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampResponse>> = await this.api.get(`/api/mentor/bootcamp/${id}`);
    return response.data;
  }

  async createBootcamp(data: CreateBootcampRequest): Promise<ApiResponse<BootcampResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampResponse>> = await this.api.post('/api/mentor/bootcamp', data);
    return response.data;
  }

  async updateBootcamp(id: number, data: UpdateBootcampRequest): Promise<ApiResponse<BootcampResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampResponse>> = await this.api.put(`/api/mentor/bootcamp/${id}`, data);
    return response.data;
  }

  async deleteBootcamp(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/bootcamp/${id}`);
    return response.data;
  }

  async uploadBootcampImage(id: number, file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response: AxiosResponse<ApiResponse<string>> = await this.api.post(
      `/api/mentor/bootcamp/${id}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getBootcampApplications(bootcampId: number, page: number = 1, pageSize: number = 20): Promise<ApiResponse<PagedResult<BootcampApplicationResponse>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<BootcampApplicationResponse>>> = await this.api.get(`/api/mentor/bootcamp/${bootcampId}/applications`, {
      params: { page, pageSize }
    });
    return response.data;
  }

  async updateBootcampApplication(applicationId: number, data: UpdateBootcampApplicationRequest): Promise<ApiResponse<BootcampApplicationResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampApplicationResponse>> = await this.api.put(`/api/mentor/bootcamp/applications/${applicationId}`, data);
    return response.data;
  }

  // Mentor Service endpoints
  async getMentorServices(mentorId?: number): Promise<ApiResponse<MentorServiceResponse[]>> {
    const url = mentorId ? `/api/mentor/service/mentor/${mentorId}` : '/api/mentor/service';
    const response: AxiosResponse<ApiResponse<MentorServiceResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorBootcamps(mentorId?: number): Promise<ApiResponse<BootcampResponse[]>> {
    const url = mentorId ? `/api/mentor/bootcamp/mentor/${mentorId}` : '/api/mentor/bootcamp';
    const response: AxiosResponse<ApiResponse<BootcampResponse[]>> = await this.api.get(url);
    return response.data;
  }

  async getMentorService(id: number): Promise<ApiResponse<MentorServiceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorServiceResponse>> = await this.api.get(`/api/mentor/service/${id}`);
    return response.data;
  }

  async createMentorService(data: CreateMentorServiceRequest): Promise<ApiResponse<MentorServiceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorServiceResponse>> = await this.api.post('/api/mentor/service', data);
    return response.data;
  }

  async updateMentorService(id: number, data: UpdateMentorServiceRequest): Promise<ApiResponse<MentorServiceResponse>> {
    const response: AxiosResponse<ApiResponse<MentorServiceResponse>> = await this.api.put(`/api/mentor/service/${id}`, data);
    return response.data;
  }

  async deleteMentorService(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentor/service/${id}`);
    return response.data;
  }


  // Mentor Search endpoints
  async searchMentors(request: MentorSearchRequest): Promise<ApiResponse<PagedResult<MentorSearchResponse>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<MentorSearchResponse>>> = await this.api.post('/api/mentor/search', request);
    return response.data;
  }

  getMentorProfileImageUrl(mentorId: number): string {
    return `${ApiDefinition.apiUrl}/api/mentor/search/${mentorId}/profile-image`;
  }

  getBootcampImageUrl(bootcampId: number): string {
    return `${ApiDefinition.apiUrl}/api/bootcamps/${bootcampId}/image`;
  }

  async getMentorSearchFilters(): Promise<ApiResponse<MentorSearchFilters>> {
    const response: AxiosResponse<ApiResponse<MentorSearchFilters>> = await this.api.get('/api/mentor/search/filters');
    return response.data;
  }

  async getPopularMentors(): Promise<ApiResponse<MentorSearchResponse[]>> {
    const response: AxiosResponse<ApiResponse<MentorSearchResponse[]>> = await this.api.get('/api/mentor/search/popular', { timeout: 15000 });
    return response.data;
  }

  async getMentorDetail(id: number): Promise<ApiResponse<MentorDetailResponse>> {
    const response: AxiosResponse<ApiResponse<MentorDetailResponse>> = await this.api.get(`/api/mentor/search/${id}`);
    return response.data;
  }

  async getMentorRatings(mentorIds: number[]): Promise<ApiResponse<Record<number, MentorRatingResponse>>> {
    const response: AxiosResponse<ApiResponse<Record<number, MentorRatingResponse>>> = await this.api.post('/api/mentor/search/ratings', mentorIds);
    return response.data;
  }

  async getMentorAvailability(mentorId: number): Promise<ApiResponse<MentorAvailabilityResponse[]>> {
    const response: AxiosResponse<ApiResponse<MentorAvailabilityResponse[]>> = await this.api.get(`/api/mentor/search/${mentorId}/availability`);
    return response.data;
  }

  async getMentorPublishedBootcamps(mentorId: number): Promise<ApiResponse<BootcampResponse[]>> {
    const response: AxiosResponse<ApiResponse<BootcampResponse[]>> = await this.api.get(`/api/mentor/search/${mentorId}/published-bootcamps`);
    return response.data;
  }

  // Refresh Token endpoints
  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/api/auth/refresh-token', { refreshToken });
    return response.data;
  }

  async revokeToken(refreshToken: string): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.post('/api/auth/revoke-token', { refreshToken });
    return response.data;
  }

  // Bootcamp Services
  async searchBootcamps(params: {
    search?: string;
    category?: string;
    location?: string;
    isOnline?: boolean;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<PagedResult<BootcampSearchResponse>>> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.location) queryParams.append('location', params.location);
    if (params.isOnline !== undefined) queryParams.append('isOnline', params.isOnline.toString());
    if (params.minPrice !== undefined) queryParams.append('minPrice', params.minPrice.toString());
    if (params.maxPrice !== undefined) queryParams.append('maxPrice', params.maxPrice.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response: AxiosResponse<ApiResponse<PagedResult<BootcampSearchResponse>>> = await this.api.get(`/api/bootcamps?${queryParams}`);
    return response.data;
  }

  async getBootcampDetail(id: number): Promise<ApiResponse<BootcampDetailResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampDetailResponse>> = await this.api.get(`/api/bootcamps/${id}`);
    return response.data;
  }

  /** Sadece bu bootcamp'e onaylanmış katılımcı (Mentee) ise toplantı linki döner. */
  async getBootcampMeetingLink(bootcampId: number): Promise<ApiResponse<string | null>> {
    const response: AxiosResponse<ApiResponse<string | null>> = await this.api.get(`/api/bootcamps/${bootcampId}/meeting-link`);
    return response.data;
  }

  async getBootcampFilters(): Promise<ApiResponse<BootcampFiltersResponse>> {
    const response: AxiosResponse<ApiResponse<BootcampFiltersResponse>> = await this.api.get('/api/bootcamps/filters');
    return response.data;
  }

  // Bootcamp Application endpoints
  async applyToBootcamp(bootcampId: number, application: BootcampApplicationRequest): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/bootcamps/${bootcampId}/apply`, application);
    return response.data;
  }

  async checkBootcampApplication(bootcampId: number): Promise<ApiResponse<boolean>> {
    const response: AxiosResponse<ApiResponse<boolean>> = await this.api.get(`/api/bootcamps/${bootcampId}/application-status`);
    return response.data;
  }

  async getMyBootcampApplications(
    page: number = 1, 
    pageSize: number = 10, 
    search?: string, 
    status?: string
  ): Promise<ApiResponse<PagedResult<BootcampApplicationResponse>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    
    const response: AxiosResponse<ApiResponse<PagedResult<BootcampApplicationResponse>>> = await this.api.get(`/api/bootcamps/my-applications?${params}`);
    return response.data;
  }

  async cancelBootcampApplication(applicationId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/bootcamps/my-applications/${applicationId}`);
    return response.data;
  }

  async getBootcampParticipants(
    bootcampId: number,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    status?: string
  ): Promise<ApiResponse<PagedResult<BootcampApplicationResponse>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    
    const response: AxiosResponse<ApiResponse<PagedResult<BootcampApplicationResponse>>> = await this.api.get(`/api/bootcamps/${bootcampId}/participants?${params}`);
    return response.data;
  }

  // Mentor Booking endpoints
  async createMentorBooking(booking: MentorBookingRequest): Promise<ApiResponse<MentorBookingResponse>> {
    const response: AxiosResponse<ApiResponse<MentorBookingResponse>> = await this.api.post('/api/mentor-bookings', booking);
    return response.data;
  }



  async getMentorBookingsByMentor(mentorId: number, page: number = 1, pageSize: number = 20): Promise<ApiResponse<PagedResult<MentorBookingResponse>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<MentorBookingResponse>>> = await this.api.get(`/api/mentor-bookings/mentor/${mentorId}`, {
      params: { page, pageSize }
    });
    return response.data;
  }

  async getMentorBookingsByMentee(
    page: number = 1,
    pageSize: number = 20,
    options?: { scheduleConflictsOnly?: boolean }
  ): Promise<ApiResponse<PagedResult<MentorBookingResponse>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<MentorBookingResponse>>> = await this.api.get('/api/mentee-bookings/my-bookings', {
      params: {
        page,
        pageSize,
        ...(options?.scheduleConflictsOnly ? { scheduleConflictsOnly: true } : {}),
      }
    });
    return response.data;
  }

  async updateMentorBookingStatus(bookingId: number, status: string): Promise<ApiResponse<MentorBookingResponse>> {
    const response: AxiosResponse<ApiResponse<MentorBookingResponse>> = await this.api.patch(`/api/mentor-bookings/${bookingId}/status`, { status });
    return response.data;
  }

  async cancelMentorBooking(bookingId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/mentor-bookings/${bookingId}`);
    return response.data;
  }

  // Company Methods
  async getCompanyProfile(): Promise<ApiResponse<CompanyProfileResponse>> {
    const response: AxiosResponse<ApiResponse<CompanyProfileResponse>> = await this.api.get('/api/company/profile');
    return response.data;
  }

  async updateCompanyProfile(data: UpdateCompanyProfileRequest): Promise<ApiResponse<CompanyProfileResponse>> {
    const response: AxiosResponse<ApiResponse<CompanyProfileResponse>> = await this.api.put('/api/company/profile', data);
    return response.data;
  }

  async uploadCompanyLogo(file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<ApiResponse<string>> = await this.api.post('/api/company/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async changeCompanyPassword(data: ChangePasswordRequest): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/api/company/change-password', data);
    return response.data;
  }

  async requestCompanyEmailVerification(data: { newEmail: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/company/request-email-verification', data);
    return response.data;
  }

  async verifyCompanyEmail(data: { token: string; code: string; password: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/company/verify-email', data);
    return response.data;
  }

  // Mentor Password & Email Change
  async changeMentorPassword(data: ChangePasswordRequest): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/api/mentor/profile/change-password', data);
    return response.data;
  }

  async requestMentorEmailVerification(data: { newEmail: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentor/profile/request-email-verification', data);
    return response.data;
  }

  async verifyMentorEmail(data: { token: string; code: string; password: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentor/profile/verify-email', data);
    return response.data;
  }

  // Mentee Password & Email Change
  async changeMenteePassword(data: ChangePasswordRequest): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/api/mentee/change-password', data);
    return response.data;
  }

  async requestMenteeEmailVerification(data: { newEmail: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentee/request-email-verification', data);
    return response.data;
  }

  async verifyMenteeEmail(data: { token: string; code: string; password: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/mentee/verify-email', data);
    return response.data;
  }

  // Mentee Search Methods
  async searchMentees(params: MenteeSearchRequest): Promise<ApiResponse<PagedResult<MenteeSearchResponse>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());
    
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.category) queryParams.append('category', params.category);
    if (params.skill) queryParams.append('skill', params.skill);
    if (params.approvedSkill) queryParams.append('approvedSkill', params.approvedSkill);
    if (params.project) queryParams.append('project', params.project);
    if (params.bootcampTopic) queryParams.append('bootcampTopic', params.bootcampTopic);
    if (params.mentorId) queryParams.append('mentorId', params.mentorId.toString());
    if (params.minMentorHours) queryParams.append('minMentorHours', params.minMentorHours.toString());
    if (params.maxMentorHours) queryParams.append('maxMentorHours', params.maxMentorHours.toString());
    if (params.minExperience) queryParams.append('minExperience', params.minExperience.toString());
    if (params.maxExperience) queryParams.append('maxExperience', params.maxExperience.toString());
    if (params.location) queryParams.append('location', params.location);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const response: AxiosResponse<ApiResponse<PagedResult<MenteeSearchResponse>>> = await this.api.get(`/api/MenteeSearch?${queryParams}`);
    return response.data;
  }

  async getMenteeSearchFilters(): Promise<ApiResponse<MenteeSearchFilters>> {
    const response: AxiosResponse<ApiResponse<MenteeSearchFilters>> = await this.api.get('/api/MenteeSearch/filters');
    return response.data;
  }

  // Notifications
  async getNotifications(page = 1, pageSize = 20): Promise<ApiResponse<{ data: NotificationItem[]; totalCount: number; pageNumber: number; pageSize: number }>> {
    const response = await this.api.get(`/api/notifications?page=${page}&pageSize=${pageSize}`);
    return response.data;
  }
  async getNotificationUnreadCount(): Promise<ApiResponse<number>> {
    const response = await this.api.get('/api/notifications/unread-count');
    return response.data;
  }
  async markNotificationRead(id: number): Promise<ApiResponse<unknown>> {
    const response = await this.api.patch(`/api/notifications/${id}/read`);
    return response.data;
  }
  async markAllNotificationsRead(): Promise<ApiResponse<unknown>> {
    const response = await this.api.patch('/api/notifications/read-all');
    return response.data;
  }

  // Mentee Detail Methods
  async getMenteeDetail(menteeId: number): Promise<ApiResponse<MenteeDetailResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeDetailResponse>> = await this.api.get(`/api/mentee/detail/${menteeId}`);
    return response.data;
  }

  async getMenteeSkillsById(menteeId: number): Promise<ApiResponse<MenteeSkillResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeSkillResponse[]>> = await this.api.get(`/api/mentee/${menteeId}/skills`);
    return response.data;
  }

  async getMenteeEducationsById(menteeId: number): Promise<ApiResponse<MenteeEducationResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeEducationResponse[]>> = await this.api.get(`/api/mentee/${menteeId}/educations`);
    return response.data;
  }

  async getMenteeWorkExperiencesById(menteeId: number): Promise<ApiResponse<MenteeWorkExperienceResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeWorkExperienceResponse[]>> = await this.api.get(`/api/mentee/${menteeId}/work-experiences`);
    return response.data;
  }

  async getMenteeProjectsById(menteeId: number): Promise<ApiResponse<MenteeProjectResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeProjectResponse[]>> = await this.api.get(`/api/mentee/${menteeId}/projects`);
    return response.data;
  }

  async getMenteeCertificatesById(menteeId: number): Promise<ApiResponse<MenteeCertificateResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeCertificateResponse[]>> = await this.api.get(`/api/mentee/${menteeId}/certificates`);
    return response.data;
  }

  // Mentee CRUD Methods for Panel
  async getMenteeEducations(): Promise<ApiResponse<MenteeEducationResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeEducationResponse[]>> = await this.api.get('/api/mentee/education');
    return response.data;
  }

  async getMenteeEducation(id: number): Promise<ApiResponse<MenteeEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeEducationResponse>> = await this.api.get(`/api/mentee/education/${id}`);
    return response.data;
  }

  async createMenteeEducation(data: CreateMenteeEducationRequest): Promise<ApiResponse<MenteeEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeEducationResponse>> = await this.api.post('/api/mentee/education', data);
    return response.data;
  }

  async updateMenteeEducation(id: number, data: UpdateMenteeEducationRequest): Promise<ApiResponse<MenteeEducationResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeEducationResponse>> = await this.api.put(`/api/mentee/education/${id}`, data);
    return response.data;
  }

  async deleteMenteeEducation(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentee/education/${id}`);
    return response.data;
  }

  async getMenteeProjects(): Promise<ApiResponse<MenteeProjectResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeProjectResponse[]>> = await this.api.get('/api/mentee/project');
    return response.data;
  }

  async getMenteeProject(id: number): Promise<ApiResponse<MenteeProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeProjectResponse>> = await this.api.get(`/api/mentee/project/${id}`);
    return response.data;
  }

  async createMenteeProject(data: CreateMenteeProjectRequest): Promise<ApiResponse<MenteeProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeProjectResponse>> = await this.api.post('/api/mentee/project', data);
    return response.data;
  }

  async updateMenteeProject(id: number, data: UpdateMenteeProjectRequest): Promise<ApiResponse<MenteeProjectResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeProjectResponse>> = await this.api.put(`/api/mentee/project/${id}`, data);
    return response.data;
  }

  async deleteMenteeProject(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentee/project/${id}`);
    return response.data;
  }

  // Admin Company Methods
  async getAdminCompanies(page: number = 1, pageSize: number = 10, firmStatus?: number, search?: string, sortBy?: string, sortOrder?: string): Promise<PagedApiResponse<any>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (firmStatus !== undefined && firmStatus !== null) params.append('firmStatus', String(firmStatus));
    if (search?.trim()) params.append('search', search.trim());
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    const response = await this.api.get(`/api/admin/companies?${params}`);
    return response.data;
  }

  async updateAdminCompany(id: number, data: UpdateAdminCompanyRequest): Promise<ApiResponse<object>> {
    const response: AxiosResponse<ApiResponse<object>> = await this.api.put(`/api/admin/companies/${id}`, data);
    return response.data;
  }

  async updateCompanyStatus(id: number, status: number): Promise<ApiResponse<object>> {
    const response: AxiosResponse<ApiResponse<object>> = await this.api.put(`/api/admin/companies/${id}/status`, status);
    return response.data;
  }

  async getMenteeSkills(): Promise<ApiResponse<MenteeSkillResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeSkillResponse[]>> = await this.api.get('/api/mentee/skill');
    return response.data;
  }

  async getMenteeSkill(id: number): Promise<ApiResponse<MenteeSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeSkillResponse>> = await this.api.get(`/api/mentee/skill/${id}`);
    return response.data;
  }

  async createMenteeSkill(data: CreateMenteeSkillRequest): Promise<ApiResponse<MenteeSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeSkillResponse>> = await this.api.post('/api/mentee/skill', data);
    return response.data;
  }

  async updateMenteeSkill(id: number, data: UpdateMenteeSkillRequest): Promise<ApiResponse<MenteeSkillResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeSkillResponse>> = await this.api.put(`/api/mentee/skill/${id}`, data);
    return response.data;
  }

  async deleteMenteeSkill(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentee/skill/${id}`);
    return response.data;
  }

  async getMenteeWorkExperiences(): Promise<ApiResponse<MenteeWorkExperienceResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeWorkExperienceResponse[]>> = await this.api.get('/api/mentee/work-experience');
    return response.data;
  }

  async getMenteeWorkExperience(id: number): Promise<ApiResponse<MenteeWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeWorkExperienceResponse>> = await this.api.get(`/api/mentee/work-experience/${id}`);
    return response.data;
  }

  async createMenteeWorkExperience(data: CreateMenteeWorkExperienceRequest): Promise<ApiResponse<MenteeWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeWorkExperienceResponse>> = await this.api.post('/api/mentee/work-experience', data);
    return response.data;
  }

  async updateMenteeWorkExperience(id: number, data: UpdateMenteeWorkExperienceRequest): Promise<ApiResponse<MenteeWorkExperienceResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeWorkExperienceResponse>> = await this.api.put(`/api/mentee/work-experience/${id}`, data);
    return response.data;
  }

  async deleteMenteeWorkExperience(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentee/work-experience/${id}`);
    return response.data;
  }

  async getMenteeCertificates(): Promise<ApiResponse<MenteeCertificateResponse[]>> {
    const response: AxiosResponse<ApiResponse<MenteeCertificateResponse[]>> = await this.api.get('/api/mentee/certificate');
    return response.data;
  }

  async getMenteeCertificate(id: number): Promise<ApiResponse<MenteeCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeCertificateResponse>> = await this.api.get(`/api/mentee/certificate/${id}`);
    return response.data;
  }

  async createMenteeCertificate(data: CreateMenteeCertificateRequest): Promise<ApiResponse<MenteeCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeCertificateResponse>> = await this.api.post('/api/mentee/certificate', data);
    return response.data;
  }

  async updateMenteeCertificate(id: number, data: UpdateMenteeCertificateRequest): Promise<ApiResponse<MenteeCertificateResponse>> {
    const response: AxiosResponse<ApiResponse<MenteeCertificateResponse>> = await this.api.put(`/api/mentee/certificate/${id}`, data);
    return response.data;
  }

  async deleteMenteeCertificate(id: number): Promise<ApiResponse<string>> {
    const response: AxiosResponse<ApiResponse<string>> = await this.api.delete(`/api/mentee/certificate/${id}`);
    return response.data;
  }


  // Job Posting Methods
  async getJobPostings(params: { page: number; pageSize: number; searchText?: string }): Promise<ApiResponse<PagedResult<JobPostingResponse>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());
    if (params.searchText) queryParams.append('searchText', params.searchText);

    const response: AxiosResponse<ApiResponse<PagedResult<JobPostingResponse>>> = await this.api.get(`/api/jobposting?${queryParams}`);
    return response.data;
  }

  async getJobPosting(id: number): Promise<ApiResponse<JobPostingResponse>> {
    const response: AxiosResponse<ApiResponse<JobPostingResponse>> = await this.api.get(`/api/jobposting/${id}`);
    return response.data;
  }

  async createJobPosting(data: CreateJobPostingRequest): Promise<ApiResponse<JobPostingResponse>> {
    const response: AxiosResponse<ApiResponse<JobPostingResponse>> = await this.api.post('/api/jobposting', data);
    return response.data;
  }

  async updateJobPosting(id: number, data: UpdateJobPostingRequest): Promise<ApiResponse<JobPostingResponse>> {
    const response: AxiosResponse<ApiResponse<JobPostingResponse>> = await this.api.put(`/api/jobposting/${id}`, data);
    return response.data;
  }

  async deleteJobPosting(id: number): Promise<ApiResponse<object>> {
    const response: AxiosResponse<ApiResponse<object>> = await this.api.delete(`/api/jobposting/${id}`);
    return response.data;
  }

  // Job Application Methods
  async getJobApplications(jobPostingId: number, params?: { page?: number; pageSize?: number; status?: string }): Promise<ApiResponse<PagedResult<JobApplicationResponse>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.status) queryParams.append('status', params.status);

    const response: AxiosResponse<ApiResponse<PagedResult<JobApplicationResponse>>> = await this.api.get(`/api/jobapplication/job-posting/${jobPostingId}?${queryParams}`);
    return response.data;
  }

  async updateJobApplication(id: number, data: UpdateJobApplicationRequest): Promise<ApiResponse<JobApplicationResponse>> {
    const response: AxiosResponse<ApiResponse<JobApplicationResponse>> = await this.api.put(`/api/jobapplication/${id}`, data);
    return response.data;
  }

  // Interview Methods
  async getInterviews(jobApplicationId: number): Promise<ApiResponse<InterviewResponse[]>> {
    const response: AxiosResponse<ApiResponse<InterviewResponse[]>> = await this.api.get(`/api/interview/job-application/${jobApplicationId}`);
    return response.data;
  }

  async getMyInterviews(page: number = 1, pageSize: number = 50): Promise<ApiResponse<PagedResult<InterviewResponse>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<InterviewResponse>>> = await this.api.get('/api/interview/my-interviews', {
      params: { page, pageSize },
    });
    return response.data;
  }


  // Public Job Posting Methods
  async getPublicJobPostings(params: PublicJobPostingSearchRequest): Promise<ApiResponse<PagedResult<PublicJobPostingResponse>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());
    
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.minExperience != null) queryParams.append('minExperience', params.minExperience.toString());
    if (params.maxExperience != null) queryParams.append('maxExperience', params.maxExperience.toString());
    if (params.minSalary) queryParams.append('minSalary', params.minSalary.toString());
    if (params.maxSalary) queryParams.append('maxSalary', params.maxSalary.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response: AxiosResponse<ApiResponse<PagedResult<PublicJobPostingResponse>>> = await this.api.get(`/api/jobposting/public?${queryParams}`);
    return response.data;
  }

  async getPublicJobPosting(id: number): Promise<ApiResponse<PublicJobPostingDetailResponse>> {
    const response: AxiosResponse<ApiResponse<PublicJobPostingDetailResponse>> = await this.api.get(`/api/jobposting/public/${id}`);
    return response.data;
  }

  async getJobPostingFilters(): Promise<ApiResponse<JobPostingFiltersResponse>> {
    const response: AxiosResponse<ApiResponse<JobPostingFiltersResponse>> = await this.api.get('/api/jobposting/public/filters');
    return response.data;
  }

  async applyToJob(jobPostingId: number, formData: FormData): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/jobapplication/apply/${jobPostingId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getMyApplicationJobIds(): Promise<ApiResponse<number[]>> {
    const response = await this.api.get<ApiResponse<number[]>>('/api/jobapplication/my-application-ids');
    return response.data;
  }

  async getMyJobApplications(page: number = 1, pageSize: number = 20): Promise<ApiResponse<PagedResult<any>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<any>>> = await this.api.get('/api/jobapplication/my-applications', {
      params: { page, pageSize }
    });
    return response.data;
  }

  async getCompanyJobApplications(filter?: CandidateFilterRequest): Promise<ApiResponse<JobApplicationResponse[]>> {
    if (filter) {
      const response: AxiosResponse<ApiResponse<JobApplicationResponse[]>> = await this.api.post('/api/jobapplication/company-applications/search', filter);
      return response.data;
    } else {
      const response: AxiosResponse<ApiResponse<JobApplicationResponse[]>> = await this.api.get('/api/jobapplication/company-applications');
      return response.data;
    }
  }

  async updateJobApplicationStatus(applicationId: number, status: string, adminNotes?: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/jobapplication/update-status/${applicationId}`, {
      status,
      adminNotes
    });
    return response.data;
  }

  async getCompanyInterviews(page: number = 1, pageSize: number = 20): Promise<ApiResponse<PagedResult<any>>> {
    const response: AxiosResponse<ApiResponse<PagedResult<any>>> = await this.api.get('/api/interview/company-interviews', {
      params: { page, pageSize }
    });
    return response.data;
  }

  async createInterview(interviewData: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/interview/create', interviewData);
    return response.data;
  }

  async updateInterview(interviewId: number, interviewData: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/interview/update/${interviewId}`, interviewData);
    return response.data;
  }

  async deleteInterview(interviewId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/interview/delete/${interviewId}`);
    return response.data;
  }

  // Candidate Favorites Management
  async getCandidateFavorites(filter?: CandidateFilterRequest): Promise<ApiResponse<CandidateFavoriteResponse[]>> {
    if (filter) {
      const response: AxiosResponse<ApiResponse<CandidateFavoriteResponse[]>> = await this.api.post('/api/candidate-favorites/search', filter);
      return response.data;
    } else {
      const response: AxiosResponse<ApiResponse<CandidateFavoriteResponse[]>> = await this.api.get('/api/candidate-favorites');
      return response.data;
    }
  }

  async addToFavorites(data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/candidate-favorites', data);
    return response.data;
  }

  async removeFromFavorites(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/candidate-favorites/${id}`);
    return response.data;
  }

  // Candidate Pool Management
  async getCandidatePool(filter?: CandidateFilterRequest): Promise<ApiResponse<CandidatePoolResponse[]>> {
    if (filter) {
      const response: AxiosResponse<ApiResponse<CandidatePoolResponse[]>> = await this.api.post('/api/candidate-pool/search', filter);
      return response.data;
    } else {
      const response: AxiosResponse<ApiResponse<CandidatePoolResponse[]>> = await this.api.get('/api/candidate-pool');
      return response.data;
    }
  }

  async addToPool(data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/candidate-pool', data);
    return response.data;
  }

  async updatePoolStatus(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/candidate-pool/${id}/status`, data);
    return response.data;
  }

  async removeFromPool(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/candidate-pool/${id}`);
    return response.data;
  }


  // Admin Methods
  async getAdminMentees(page: number = 1, pageSize: number = 10, sortBy?: string, sortOrder?: string, search?: string): Promise<PagedApiResponse<any>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (search) params.append('search', search);
    const response = await this.api.get(`/api/admin/mentees?${params}`);
    return response.data;
  }

  async getAdminMentors(page: number = 1, pageSize: number = 10, sortBy?: string, sortOrder?: string, search?: string): Promise<PagedApiResponse<any>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (search) params.append('search', search);
    const response = await this.api.get(`/api/admin/mentors?${params}`);
    return response.data;
  }

  async updateAdminMentee(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/mentees/${id}`, data);
    return response.data;
  }

  async updateAdminMentor(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/mentors/${id}`, data);
    return response.data;
  }

  async deleteAdminMentee(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/mentees/${id}`);
    return response.data;
  }

  async deleteAdminMentor(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/mentors/${id}`);
    return response.data;
  }

  async resetUserPassword(id: number, newPassword: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/admin/reset-password`, {
      userId: id,
      newPassword: newPassword
    });
    return response.data;
  }

  // Category Methods
  async getAdminCategories(page: number = 1, pageSize: number = 10): Promise<PagedApiResponse<any>> {
    const response = await this.api.get(`/api/admin/categories?page=${page}&pageSize=${pageSize}`);
    return response.data;
  }

  async createCategory(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/admin/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/categories/${id}`);
    return response.data;
  }

  async uploadCategoryImage(id: number, imageFile: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    const response = await this.api.post(`/api/admin/categories/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Public Category Methods
  async getPopularCategories(limit: number = 8): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/api/categories/popular?limit=${limit}`, { timeout: 15000 });
    return response.data;
  }

  // Admin Dashboard Methods
  async getAdminStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/api/admin/stats');
    return response.data;
  }

  async getRecentMentees(limit: number = 5): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/api/admin/recent-mentees?limit=${limit}`);
    return response.data;
  }

  async getRecentMentors(limit: number = 5): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/api/admin/recent-mentors?limit=${limit}`);
    return response.data;
  }

  // Mentor Appointments Methods
  async getMentorBookings(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/bookings');
    return response.data;
  }

  async updateBookingStatus(bookingId: number, status: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/mentor/bookings/${bookingId}/status`, { status });
    return response.data;
  }

  async completeBooking(bookingId: number): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/mentor/bookings/${bookingId}/complete`);
    return response.data;
  }

  async getMentorBootcampApplications(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/bootcamp-applications');
    return response.data;
  }

  async getApprovedMentees(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/api/mentor/approved-mentees');
    return response.data;
  }

  async approveMenteeSkill(skillId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/mentor/approve-skill/${skillId}`);
    return response.data;
  }

  async removeSkillApproval(skillId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/mentor/approve-skill/${skillId}`);
    return response.data;
  }

  async updateBootcampApplicationStatus(applicationId: number, status: string, mentorNotes?: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/mentor/bootcamp-applications/${applicationId}/status`, { 
      status, 
      mentorNotes 
    });
    return response.data;
  }

  async completeBootcampApplication(applicationId: number): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/mentor/bootcamp-applications/${applicationId}/complete`);
    return response.data;
  }

  // Referans talepleri için API fonksiyonları
  async getMenteeApprovedApplications(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/approved-applications');
    return response.data;
  }

  async getMentorApprovedApplications(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/profile/approved-applications');
    return response.data;
  }

  // Referans talebi gönderme fonksiyonları
  async createMenteeReferenceRequest(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentee/reference-request', data);
    return response.data;
  }

  async createMentorReferenceRequest(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentor/profile/reference-request', data);
    return response.data;
  }

  // Gelen referans talepleri
  async getMenteeIncomingReferenceRequests(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/incoming-reference-requests');
    return response.data;
  }

  async getMentorIncomingReferenceRequests(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/profile/incoming-reference-requests');
    return response.data;
  }

  // Favori/Referans (MentorFavouriteRequest) API
  async getFavouriteCandidates(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/profile/favourites/candidates');
    return response.data;
  }
  async getSentFavouriteRequests(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/profile/favourites/requests');
    return response.data;
  }
  async createFavouriteRequest(data: { type: number; applicationId: number; targetUserId: number; message?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentor/profile/favourites/request', data);
    return response.data;
  }
  async getIncomingFavouriteRequests(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/favourites/incoming');
    return response.data;
  }
  async getReferrerMentors(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/favourites/referrers');
    return response.data;
  }
  async acceptFavouriteRequest(id: number): Promise<ApiResponse<unknown>> {
    const response = await this.api.post(`/api/mentee/favourites/requests/${id}/accept`);
    return response.data;
  }
  async rejectFavouriteRequest(id: number): Promise<ApiResponse<unknown>> {
    const response = await this.api.post(`/api/mentee/favourites/requests/${id}/reject`);
    return response.data;
  }

  // Referans oluşturma
  async createMenteeReference(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentee/create-reference', data);
    return response.data;
  }

  async createMentorReference(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentor/profile/create-reference', data);
    return response.data;
  }

  async getMenteeMentorRatingApplications(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/mentor-rating-applications');
    return response.data;
  }

  async rateMentor(data: {
    content: string;
    rating: number;
    title?: string;
    type: number;
    applicationId: number;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/mentee/rate-mentor', data);
    return response.data;
  }

  // Bana yazılan referanslar
  async getMenteeReceivedReferences(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentee/received-references');
    return response.data;
  }

  async getMentorReceivedReferences(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/profile/received-references');
    return response.data;
  }

  // Advertisement endpoints (Mentor)
  async getAdvertisementSlots(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/advertisements/slots');
    return response.data;
  }

  async getMyAdvertisementRequests(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/mentor/advertisements/requests');
    return response.data;
  }

  async createAdvertisementRequest(formData: FormData): Promise<ApiResponse<ParamPaymentInitResponse>> {
    const response = await this.api.post('/api/mentor/advertisements/requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteAdvertisementRequest(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/api/mentor/advertisements/requests/${id}`);
    return response.data;
  }

  // Advertisement endpoints (Public)
  async getActiveAdvertisements(type?: number, pagePath?: string, position?: AdvertisementPosition): Promise<ApiResponse<AdvertisementResponse[]>> {
    const params: any = {};
    if (type) params.type = type.toString();
    if (pagePath) params.pagePath = pagePath;
    if (position !== undefined) params.position = position.toString();
    const response = await this.api.get('/api/advertisements/active', { params });
    return response.data;
  }

  async getPublicAdvertisementSlots(pagePath?: string, position?: AdvertisementPosition): Promise<ApiResponse<AdvertisementSlotResponse[]>> {
    const params: { pagePath?: string; position?: string } = {};
    if (pagePath) params.pagePath = pagePath;
    if (position !== undefined) params.position = position.toString();
    const response: AxiosResponse<ApiResponse<AdvertisementSlotResponse[]>> = await this.api.get('/api/advertisements/slots', { params });
    return response.data;
  }

  async recordAdvertisementClick(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/api/advertisements/${id}/click`);
    return response.data;
  }

  // Advertisement endpoints (Admin)
  async getAdminAdvertisementSlots(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/admin/advertisements/slots');
    return response.data;
  }

  async createAdvertisementSlot(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/admin/advertisements/slots', data);
    return response.data;
  }

  async updateAdvertisementSlot(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/advertisements/slots/${id}`, data);
    return response.data;
  }

  async deleteAdvertisementSlot(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/api/admin/advertisements/slots/${id}`);
    return response.data;
  }

  async getAdminAdvertisementRequests(status?: number): Promise<ApiResponse<any[]>> {
    const params = status ? { status: status.toString() } : {};
    const response = await this.api.get('/api/admin/advertisements/requests', { params });
    return response.data;
  }

  async approveAdvertisementRequest(id: number, requestedWeekStartDate: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/api/admin/advertisements/requests/${id}/approve`, {
      requestId: id
    });
    return response.data;
  }

  async rejectAdvertisementRequest(id: number, rejectionReason?: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/api/admin/advertisements/requests/${id}/reject`, {
      requestId: id,
      rejectionReason
    });
    return response.data;
  }

  // Subscription endpoints
  async getCurrentSubscription(): Promise<ApiResponse<CurrentSubscriptionResponse>> {
    const response = await this.api.get('/api/subscription/current');
    return response.data;
  }

  async getSubscriptionHistory(): Promise<ApiResponse<SubscriptionResponse[]>> {
    const response = await this.api.get('/api/subscription/history');
    return response.data;
  }

  async getSubscriptionPricing(): Promise<ApiResponse<SubscriptionPricingResponse>> {
    const response = await this.api.get('/api/subscription/pricing');
    return response.data;
  }

  // Payment callback endpoint (AllowAnonymous)
  async processPaymentCallback(params: {
    Siparis_ID?: string;
    Sonuc?: string;
    Sonuc_Str?: string;
    Islem_ID?: string;
    Hash?: string;
    fromFrontend?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.api.get('/api/subscription/payment/success', { params });
    return response.data;
  }

  // Password reset endpoints
  async forgotPassword(email: string): Promise<ApiResponse<string>> {
    const response = await this.api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, code: string, newPassword: string): Promise<ApiResponse<string>> {
    const response = await this.api.post('/api/auth/reset-password', { token, code, newPassword });
    return response.data;
  }

  async verifyRegistration(token: string, code: string): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/api/auth/verify-registration', { token, code });
    return response.data;
  }

  async createSubscription(request: CreateSubscriptionRequest): Promise<ApiResponse<ParamPaymentInitResponse>> {
    const response = await this.api.post('/api/subscription/create', request);
    return response.data;
  }

  async renewSubscription(request: RenewSubscriptionRequest): Promise<ApiResponse<ParamPaymentInitResponse>> {
    const response = await this.api.post('/api/subscription/renew', request);
    return response.data;
  }

  async cancelSubscription(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/api/subscription/cancel');
    return response.data;
  }

  /** Dev/test: Features:EnableDevFakePayments — kapalıysa 404 */
  async getDevFakePaymentEnabled(): Promise<ApiResponse<DevFakePaymentFeatureResponse>> {
    const response = await this.api.get('/api/subscription/dev/fake-payment-enabled');
    return response.data;
  }

  async devFakeCompleteSubscription(
    request: DevFakeSubscriptionCompleteRequest
  ): Promise<ApiResponse<FakePaymentResponse>> {
    const response = await this.api.post('/api/subscription/dev/fake-complete-subscription', request);
    return response.data;
  }

  async createAdvertisementDevFake(formData: FormData): Promise<ApiResponse<DevFakeAdvertisementResponse>> {
    const response = await this.api.post('/api/mentor/advertisements/dev/fake-complete-request', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Admin Subscription endpoints
  async getAllSubscriptions(page: number = 1, pageSize: number = 50, userType?: string, paymentStatus?: string): Promise<PagedApiResponse<AdminSubscriptionResponse>> {
    const params: any = { page, pageSize };
    if (userType) params.userType = userType;
    if (paymentStatus) params.paymentStatus = paymentStatus;
    const response = await this.api.get('/api/admin/subscriptions/all', { params });
    return response.data;
  }

  async getUserSubscriptions(userId: number, userType: string): Promise<ApiResponse<AdminSubscriptionResponse[]>> {
    const response = await this.api.get(`/api/admin/subscriptions/user/${userId}?userType=${userType}`);
    return response.data;
  }

  async manageUserSubscription(request: AdminManageSubscriptionRequest): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/api/admin/subscriptions/manage', request);
    return response.data;
  }

  async cancelSubscriptionAdmin(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/api/admin/subscriptions/${id}/cancel`);
    return response.data;
  }

  async getAdminSubscriptionPricing(): Promise<ApiResponse<SubscriptionPricingResponse>> {
    const response = await this.api.get('/api/admin/subscriptions/pricing');
    return response.data;
  }

  async updateAdminSubscriptionPricing(request: UpdateSubscriptionPricingRequest): Promise<ApiResponse<object>> {
    const response = await this.api.put('/api/admin/subscriptions/pricing', request);
    return response.data;
  }

  // Error Logging endpoint
  async logFrontendError(errorData: {
    message: string;
    stack?: string;
    errorType?: string;
    url?: string;
    userAgent?: string;
    userId?: string;
    userEmail?: string;
    userRole?: string;
    component?: string;
    action?: string;
    additionalData?: Record<string, any>;
    browserInfo?: string;
    screenResolution?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/api/errorlog/log', errorData);
      return response.data;
    } catch (error) {
      // Error logging hatası - sessizce devam et
      console.error('Failed to send error log:', error);
      return { success: false, message: 'Error logging failed', data: null };
    }
  }
}

export const apiService = new ApiService();
export default apiService;
